import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S07P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 07"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>NodeJS</li>
            <li>Programación Orientada a Objetos</li>
            <li>TypeScript</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide title="NodeJS" subtitle="Introducción básica" />
        <DefaultSlide title="NodeJS">
          <div>
            <p>
              NodeJS es un entorno de ejecución de JavaScript del lado del
              servidor. Permite ejecutar código JavaScript fuera del navegador.
            </p>
            <p>
              NodeJS se basa en el motor V8 de Google Chrome, que convierte el
              código JavaScript en código máquina.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            En esta clase abordaremos NodeJS como herramienta para facilitar el
            desarrollo web. Al igual que Python, podemos ejecutar aplicaciones
            de NodeJS desde nuestra consola y es en esto en lo que nos vamos a
            concentrar hoy.
          </p>
          <p>
            Podemos usar JavaScript del lado del servidor con NodeJS, pero esto
            lo veremos más adelante.
          </p>
        </section>
        <DefaultSlide title="Instalando NodeJS">
          <div>
            <p>
              NodeJS se puede instalar desde su página oficial:
              <a
                href="https://nodejs.org/en/"
                target="_blank"
                rel="noopener noreferrer"
              >
                nodejs.org
              </a>
            </p>
            <p>También podemos usar NVM para instalar NodeJS.</p>
            <p className="fragment">O bien... podemos usar devcontainers!</p>
          </div>
        </DefaultSlide>
        <section>
          <p>Hora de instalar NodeJS</p>
        </section>
        <DefaultSlide title="Ejecutando NodeJS">
          <div>
            <p>
              Para ejecutar un archivo de NodeJS, usamos el comando node seguido
              del nombre del archivo. Por ejemplo:
            </p>
            <pre>
              <code>{`node script.js`}</code>
            </pre>
            <p>Esto ejecutará el archivo script.js en el entorno de NodeJS.</p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="NodeJS y npm">
          <div style={{ fontSize: "0.9em" }}>
            <p>
              NodeJS viene con npm, que es un gestor de paquetes para
              JavaScript. npm nos permite instalar y gestionar dependencias en
              nuestros proyectos.
            </p>
            <p>
              npm también nos permite instalar paquetes globalmente, lo que
              significa que estarán disponibles en cualquier proyecto de nuestro
              sistema.
            </p>
            <p>
              npm también nos permite instalar paquetes localmente, lo que
              significa que estarán disponibles solo en el proyecto en el que
              estamos trabajando.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El archivo package.json">
          <div>
            <p>
              El archivo package.json es un archivo de configuración que
              contiene información sobre nuestro proyecto y sus dependencias.
            </p>
            <p>
              Este archivo se crea automáticamente cuando inicializamos un
              proyecto con npm init.
            </p>
            <p>También podemos crear este archivo manualmente.</p>
          </div>
        </DefaultSlide>
        <section>
          <p>Conozcamos algunas aplicaciones famosas de npm:</p>
          <ul>
            <li>
              <a
                href="https://surge.sh/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Surge.sh
              </a>
            </li>
            <li>
              <a
                href="https://github.com/The-Node-Forge/image-resizer-cli"
                target="_blank"
                rel="noopener noreferrer"
              >
                Image Resizer
              </a>
            </li>
            <li>
              <a
                href="https://www.npmjs.com/package/http-server"
                target="_blank"
                rel="noopener noreferrer"
              >
                http-server
              </a>
            </li>
            <li>
              <a
                href="https://firebearstudio.com/blog/node-js-command-line-apps-utilities.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Muchas más!
              </a>
            </li>
          </ul>
        </section>
        <section>
          <p>
            NodeJS es una herramienta muy poderosa y versátil. Nos permite
            ejecutar código JavaScript del lado del servidor y crear
            aplicaciones web completas.
          </p>
          <p>
            Veamos un ejemplo de crear una aplicación de NodeJs para obtener el
            valor del dólar de una API y compararlo con el valor del día
            anterior.
          </p>
        </section>
        <section>
          <p>
            Genial! entonces con NodeJS finalmente podemos saltar al mundo de
            React?
          </p>
          <img
            className="fragment"
            src="/images/S06/almost.jpg"
            alt="Ya casi!"
          />
        </section>
        <section>
          <p>Primero debemos aprender TypeScript!</p>
          <p className="fragment">Y un poquito de POO...</p>
        </section>
      </section>
      <section>
        <TitleSlide
          title="Programación Orientada a Objetos en JavaScript"
          subtitle="Introducción básica"
        />
        <DefaultSlide title="Programación Orientada a Objetos en JavaScript">
          <div>
            <p>
              La programación orientada a objetos (POO) es un paradigma de
              programación que utiliza "objetos" para representar datos y
              comportamientos.
            </p>
            <p>
              JavaScript permite la programación orientado a objetos, lo que
              significa que podemos crear y manipular objetos en nuestro código.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            En esta clase abordaremos la POO en JavaScript y cómo podemos
            utilizarla para crear aplicaciones más complejas y organizadas.
          </p>
          <p>
            La POO nos permite organizar nuestro código en objetos, lo que nos
            ayuda a mantener nuestro código limpio y fácil de entender.
          </p>
        </section>
        <DefaultSlide title="Clases y Objetos">
          <div>
            <p>
              En JavaScript, una clase es una plantilla para crear objetos. Una
              clase define las propiedades y métodos que tendrá un objeto.
            </p>
            <p>
              Un objeto es una instancia de una clase. Un objeto tiene sus
              propias propiedades y métodos, que pueden ser diferentes a los de
              otros objetos de la misma clase.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creando una clase">
          <div>
            <p>
              Para crear una clase en JavaScript, usamos la palabra clave class
              seguida del nombre de la clase. Por ejemplo:
            </p>
            <pre>
              <code className="language-javascript">{`class Persona {
  constructor(nombre, edad) {
    this.nombre = nombre;
    this.edad = edad;
  }
  saludar() {
    console.log("Hola, mi nombre es " + this.nombre);
  }
}`}</code>
            </pre>
            <p>
              En este ejemplo, hemos creado una clase Persona con un constructor
              y un método saludar.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creando un objeto">
          <div>
            <p>
              Para crear un objeto a partir de una clase, usamos la palabra
              clave new seguida del nombre de la clase. Por ejemplo:
            </p>
            <pre>
              <code className="language-javascript">{`let persona1 = new Persona("Juan", 30);
persona1.saludar();`}</code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El constructor en JavaScript">
          <div>
            <p>
              El constructor es un método especial para crear e inicializar un
              objeto creado con una clase.
            </p>
            <p>
              Se llama automáticamente cuando se crea un nuevo objeto de la
              clase.
            </p>
            <pre>
              <code className="language-javascript">{`class Persona {
  constructor(nombre, edad) {
    this.nombre = nombre;
    this.edad = edad;
  }
}`}</code>
            </pre>
            <p>
              En este ejemplo, el constructor toma dos parámetros, nombre y
              edad, y los asigna a las propiedades del objeto.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Getters y Setters">
          <div>
            <p>
              Los getters y setters son métodos especiales que nos permiten
              acceder y modificar las propiedades de un objeto de manera
              controlada.
            </p>
            <p>
              Un getter es un método que se utiliza para obtener el valor de una
              propiedad, mientras que un setter se utiliza para establecer el
              valor de una propiedad.
            </p>
          </div>
        </DefaultSlide>
        <CodeSlide title="Setters y Getters" language="javascript">
          {`class Persona {
  constructor(nombre) {
    this._nombre = nombre;
  }
  get nombre() {
    return this._nombre;
  }
  set nombre(nuevoNombre) {
    this._nombre = nuevoNombre.toUpperCase();
  }
}

let persona1 = new Persona("Juan");
console.log(persona1.nombre); // Juan
persona1.nombre = "Carlos";
console.log(persona1.nombre); // Carlos`}
        </CodeSlide>
        <DefaultSlide title="Herencia">
          <div>
            <p>
              La herencia es un concepto fundamental en la programación
              orientada a objetos. Nos permite crear nuevas clases basadas en
              clases existentes.
            </p>
            <p>
              En JavaScript, usamos la palabra clave extends para crear una
              clase que hereda de otra clase.
            </p>
          </div>
        </DefaultSlide>
        <CodeSlide title="Herencia" language="javascript">
          {`class Animal {
  constructor(nombre) {
    this.nombre = nombre;
  }
  hacerSonido() {
    console.log("El animal hace un sonido");
  }
}
class Perro extends Animal {
  constructor(nombre, raza) {
    super(nombre);
    this.raza = raza;
  }
  hacerSonido() {
    console.log("El perro ladra");
  }
}
let perro1 = new Perro("Rex", "Labrador");
perro1.hacerSonido(); // El perro ladra
console.log(perro1.nombre); // Rex
console.log(perro1.raza); // Labrador`}
        </CodeSlide>
        <DefaultSlide title="Polimorfismo">
          <div>
            <p>
              El polimorfismo es la capacidad de un objeto para tomar muchas
              formas. En JavaScript, esto se logra mediante la herencia y la
              sobreescritura de métodos.
            </p>
            <p>
              Esto significa que podemos usar un método de una clase base en una
              clase derivada y cambiar su comportamiento.
            </p>
          </div>
        </DefaultSlide>
        <CodeSlide title="Polimorfismo" language="javascript">
          {`class Gato extends Animal {
  constructor(nombre, color) {
    super(nombre);
    this.color = color;
  }
  hacerSonido() {
    console.log("El gato maulla");
  }
}
let perro1 = new Perro("Rex", "Labrador");
let gato1 = new Gato("Miau", "Blanco");
for (let animal of [perro1, gato1]) {
  animal.hacerSonido();
  // El perro ladra
  // El gato maulla
  console.log(animal.nombre);
  // Rex
  // Miau
  console.log(animal.raza);
  // Labrador
  // undefined
  console.log(animal.color);
  // undefined
  // Blanco
}`}
        </CodeSlide>
        <section>
          <p>
            Notaron ese monton de undefined? JavaScript permite iterar sobre el
            instancias de Animal y llamar a propiedades que no existen.
          </p>
          <img
            src="/images/S07/jsvsts.jpg"
            alt="JS vs TS"
            className="fragment"
          />
        </section>
      </section>
      <section>
        <TitleSlide title="TypeScript" subtitle="Introducción básica" />
        <DefaultSlide title="TypeScript">
          <div>
            <p>
              TypeScript es un superconjunto de JavaScript que añade tipos
              estáticos y otras características a JavaScript.
            </p>
            <p>
              TypeScript se compila a JavaScript, lo que significa que podemos
              usar TypeScript en cualquier proyecto de JavaScript.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>Hora de instalar TypeScript</p>
        </section>
        <DefaultSlide title="Instalando TypeScript">
          <div>
            <p>
              Para instalar TypeScript, usamos npm. Podemos instalarlo
              globalmente o localmente en nuestro proyecto.
            </p>
            <pre>
              <code>{`npm install -g typescript`}</code>
            </pre>
            <p>Esto instalará TypeScript globalmente en nuestro sistema.</p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ejecutando TypeScript">
          <div>
            <p>
              Para ejecutar un archivo de TypeScript, primero debemos compilarlo
              a JavaScript. Esto se hace con el comando tsc seguido del nombre
              del archivo. Por ejemplo:
            </p>
            <pre>
              <code>{`tsc script.ts`}</code>
            </pre>
            <p>Esto generará un archivo script.js con el código compilado.</p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El archivo tsconfig.json">
          <div>
            <p>
              El archivo tsconfig.json es un archivo de configuración que
              contiene información sobre nuestro proyecto y sus dependencias.
            </p>
            <p>
              Este archivo se crea automáticamente cuando inicializamos un
              proyecto con tsc --init.
            </p>
            <p>También podemos crear este archivo manualmente.</p>
          </div>
        </DefaultSlide>
        <section>
          <p>Veamos un ejemplo de TypeScript:</p>
          <pre>
            <code className="language-typescript">{`let message: string = "Hello, TypeScript!";
console.log(message);`}</code>
          </pre>
          <p>
            En este ejemplo, estamos declarando una variable message de tipo
            string y asignándole un valor. TypeScript nos permite declarar tipos
            para nuestras variables, lo que nos ayuda a evitar errores en tiempo
            de compilación.
          </p>
        </section>
        <DefaultSlide title="Características principales de TypeScript">
          <div>
            <ul>
              <li>Tipado estático</li>
              <li>Interfaces</li>
              <li>Clases y herencia</li>
              <li>Modularidad</li>
              <li>Uso de Generics</li>
              <li>Uso de Decoradores</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Tipado Estático">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El tipado estático es una característica de TypeScript que nos
              permite definir los tipos de datos que nuestras variables,
              parámetros y funciones pueden tener.
            </p>
            <p>
              Esto ayuda a detectar errores en tiempo de compilación, en lugar
              de tiempo de ejecución, lo que hace que nuestro código sea más
              seguro y fácil de mantener.
            </p>
            <pre>
              <code className="language-typescript">{`let numero: number = 42;
numero = "Hola"; // Error: Type 'string' is not assignable to type 'number'.`}</code>
            </pre>
            <p>
              En este ejemplo, TypeScript nos advierte que estamos intentando
              asignar un valor de tipo string a una variable de tipo number.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Interfaces">
          <div style={{ fontSize: "0.55em" }}>
            <p>
              Una interfaz en TypeScript es una forma de definir la estructura
              de un objeto. Nos permite especificar las propiedades y métodos
              que un objeto debe tener.
            </p>
            <p>
              Las interfaces son útiles para garantizar que los objetos cumplan
              con un contrato específico, lo que ayuda a mantener nuestro código
              consistente y fácil de entender.
            </p>
            <pre>
              <code className="language-typescript">{`interface Persona {
  nombre: string;
  edad: number;
  saludar(): void;
}

const persona: Persona = {
  nombre: "Juan",
  edad: 30,
  saludar() {
    console.log("Hola, mi nombre es " + this.nombre);
  },
};

persona.saludar();`}</code>
            </pre>
            <p>
              En este ejemplo, la interfaz <code>Persona</code> define las
              propiedades y el método que un objeto debe tener para cumplir con
              esta interfaz.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Beneficios de la Herencia en TypeScript">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              TypeScript mejora la herencia en comparación con JavaScript al
              proporcionar un sistema de tipos estáticos, lo que ayuda a
              detectar errores en tiempo de compilación.
            </p>
            <p>
              Esto asegura que las clases derivadas implementen correctamente
              las propiedades y métodos de las clases base, reduciendo errores y
              mejorando la mantenibilidad del código.
            </p>
            <p>
              Además, TypeScript permite el uso de modificadores de acceso como{" "}
              <code>public</code>, <code>private</code>, y{" "}
              <code>protected</code>, lo que proporciona un mayor control sobre
              la visibilidad de las propiedades y métodos.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ejemplo de Herencia en TypeScript">
          <div style={{ fontSize: "0.75em" }}>
            <pre>
              <code className="language-typescript">{`class Animal {
  protected nombre: string;

  constructor(nombre: string) {
    this.nombre = nombre;
  }

  hacerSonido(): void {
    console.log("El animal hace un sonido");
  }
}

class Perro extends Animal {
  private raza: string;

  constructor(nombre: string, raza: string) {
    super(nombre);
    this.raza = raza;
  }

  hacerSonido(): void {
    console.log("El perro ladra");
  }
}

const perro = new Perro("Rex", "Labrador");
perro.hacerSonido(); // El perro ladra`}</code>
            </pre>
            <p>
              En este ejemplo, el uso de <code>protected</code> y{" "}
              <code>private</code> asegura que las propiedades sean accesibles
              solo donde corresponde, mejorando la encapsulación.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Modularidad">
          <div>
            <p>
              TypeScript permite la creación de módulos, lo que nos ayuda a
              organizar nuestro código en archivos separados y reutilizables.
            </p>
            <p>
              Podemos importar y exportar módulos para compartir código entre
              diferentes archivos.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Decoradores en TypeScript">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Los decoradores en TypeScript son una característica experimental
              que nos permite añadir metadatos o modificar el comportamiento de
              clases, métodos, propiedades o parámetros.
            </p>
            <p>
              Un decorador es una función que se aplica a una declaración para
              modificarla o enriquecerla con funcionalidad adicional.
            </p>
            <p>
              En el siguiente ejemplo, el decorador <code>@Log</code> registra
              en la consola los argumentos con los que se llama al método{" "}
              <code>saludar</code>.
            </p>
          </div>
        </DefaultSlide>
        <CodeSlide title="Ejemplo de Decorador" language="typescript">
          {`function Log(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function (...args: any[]) {
    console.log("Llamando al método:", propertyKey, "con argumentos:", args);
    return originalMethod.apply(this, args);
  }
}
class Persona {
  constructor(public nombre: string) {}
  @Log
  saludar(mensaje: string) {
    console.log(mensaje, this.nombre);
  }
}
const persona = new Persona("Juan");
persona.saludar("Hola"); // Llamando al método: saludar con argumentos: [ 'Hola' ]
// Hola Juan`}
        </CodeSlide>
      </section>
      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de parte 1" />
    </>
  );
}
